
'use client';

import { useState, useMemo, useEffect, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import { GradientText } from '@/components/ui/gradient-text';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Input } from '@/components/ui/input';
import { Search, Loader2 } from 'lucide-react';

interface Faq {
  id: number;
  question: string;
  answer: string;
}

interface FaqsData {
  faqs: Faq[];
}

const API_BASE_URL = process.env.NEXT_PUBLIC_BACKEND_API_URL || 'https://api.keerainnovations.com';

async function fetchFaqs(): Promise<{
  success: boolean;
  data?: FaqsData;
  message?: string;
}> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/faq`, { next: { revalidate: 600 } });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'An unknown error occurred during fetch.' }));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();

    if (result.success) {
      return { success: true, data: { faqs: result.data } };
    } else {
      return { success: false, message: result.message };
    }
  } catch (error) {
    console.error('Error fetching FAQs:', error);
    const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred.';
    return { success: false, message: `Failed to fetch FAQs: ${errorMessage}` };
  }
}

function FaqContent() {
  const [searchTerm, setSearchTerm] = useState('');
  const [allFaqs, setAllFaqs] = useState<Faq[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadFaqs() {
      setIsLoading(true);
      setError(null);
      const result = await fetchFaqs();
      if (result.success && result.data) {
        setAllFaqs(result.data.faqs);
      } else {
        setError(result.message || 'Could not load FAQs.');
      }
      setIsLoading(false);
    }
    loadFaqs();
  }, []);

  const filteredFaqs = useMemo(() => {
    if (!searchTerm) {
      return allFaqs;
    }
    const lowercasedFilter = searchTerm.toLowerCase();
    return allFaqs.filter(faq =>
      faq.question.toLowerCase().includes(lowercasedFilter) ||
      faq.answer.toLowerCase().includes(lowercasedFilter)
    );
  }, [searchTerm, allFaqs]);

  return (
    <>
      <div className="mb-12 max-w-lg mx-auto">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search questions..."
            className="pl-10 text-base"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

       {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
        </div>
      ) : error ? (
        <div className="text-center text-destructive p-8 bg-destructive/10 rounded-lg">
          <p className='font-bold text-lg mb-2'>Error loading FAQs</p>
          <p className='text-sm'>{error}</p>
        </div>
      ) : filteredFaqs.length > 0 ? (
        <Accordion type="single" collapsible className="w-full">
          {filteredFaqs.map((faq, index) => (
            <AccordionItem value={`item-${index}`} key={index}>
              <AccordionTrigger className="text-left text-lg">{faq.question}</AccordionTrigger>
              <AccordionContent className="text-base text-muted-foreground">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      ) : (
        <div className="text-center text-muted-foreground py-16">
          <p className="text-lg font-semibold">No questions found.</p>
          <p>We couldn't find any FAQs matching your search. Please try a different keyword.</p>
        </div>
      )}
    </>
  );
}

export default function FaqPage() {
  return (
    <div className="container mx-auto max-w-4xl py-12 px-4">
      <div className="text-center mb-12">
        <h1 className="font-headline text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
          Frequently Asked <GradientText>Questions</GradientText>
        </h1>
        <p className="mt-4 max-w-2xl mx-auto text-lg text-muted-foreground">
          Have questions? We have answers. Find what you're looking for below.
        </p>
      </div>
      <Suspense fallback={<div className="flex justify-center items-center h-64"><Loader2 className="h-12 w-12 animate-spin text-primary" /></div>}>
        <FaqContent />
      </Suspense>
    </div>
  );
}
